// R58-L-07 AC5 — TanStack Virtual row-windowing primitive shared by
// FindingsPanel + DefectsPanel. Closes the AC5 deferral from PR #76
// (the original L-07 ship — see _STORIES/shipped/lyse/R58-L-07-list-widget.md).
//
// Track C.6 / SB5 / Q6 locked decision: implement TanStack Virtual now
// (14 KB gzip is acceptable; 709-row datasets are real, and AC5 is the
// last AC blocking R58-L-07 from full sign-off).
//
// Design notes:
//   - Variable-height rows. Some rows have multi-line `aiClassName`
//     copy and pending rows render a checkbox + action buttons that
//     can push them to ~70 px tall, while non-pending rows stay at
//     ~56 px. We use `measureElement` so each row reports its actual
//     height back to the virtualiser; `estimateSize` is a 64-px
//     starting guess that converges to the real height on first
//     measurement.
//   - Single scroll container. The primitive itself is the
//     `getScrollElement` target — owns `overflow: auto`. The parent
//     `ListWidget` body keeps `overflow: hidden` while a virtualised
//     panel is mounted (the panel becomes the only scroll container,
//     preserving the AC1 single-scrollbar invariant).
//   - Selection survives mount/unmount. Each row is keyed by its id
//     (Zustand store keeps the selection set keyed by id, not by React
//     ref) so a row that scrolls out and back in re-renders with the
//     correct `selected` state without any per-row subscription
//     gymnastics. This satisfies the brief's "selection store must
//     survive virtualisation" stop-criterion.
//   - Cursor pagination hook (`onScrollNearEnd`) is exposed but
//     OPTIONAL. The current panels pass `pageSize=2000` and rely on a
//     single-page response (the 709-row worst case fits comfortably).
//     A future enhancement can swap the underlying query to
//     `useInfiniteQuery` and wire `fetchNextPage` to this callback
//     without touching the primitive's contract.

import { useCallback, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { useVirtualizer, type VirtualItem } from "@tanstack/react-virtual";

export interface VirtualisedRowListProps<T> {
  /** Row payloads. Identity is by `getRowId`. */
  items: readonly T[];
  /** Stable id selector. Drives the React `key`. */
  getRowId: (item: T) => string;
  /** Per-row renderer. The virtual primitive supplies absolute
   *  positioning + the measureRef; the renderer just paints the row
   *  content. */
  renderRow: (item: T, index: number) => React.ReactNode;
  /** Estimated row height in px. The first measureElement pass
   *  refines this per-row, but `estimateSize` keeps the scrollbar
   *  proportionate before measurements land. Default 64. */
  estimateSize?: number;
  /** Pre-render this many rows above/below the viewport. Default 6 —
   *  matches TanStack docs' "feels smooth" baseline. */
  overscan?: number;
  /** Fires when the user scrolls within `nearEndRows` of the last
   *  row. Use to trigger `fetchNextPage()` once we move to
   *  `useInfiniteQuery`. Default `nearEndRows` = 4. Optional. */
  onScrollNearEnd?: () => void;
  /** Distance (in rows) from the last row that counts as "near end".
   *  Default 4. */
  nearEndRows?: number;
  /** Test seam — adds `data-testid` to the scroll container. */
  testId?: string;
  /** Optional aria attributes for the scroll container. */
  ariaLabel?: string;
}

export function VirtualisedRowList<T>({
  items,
  getRowId,
  renderRow,
  estimateSize = 64,
  overscan = 6,
  onScrollNearEnd,
  nearEndRows = 4,
  testId,
  ariaLabel,
}: VirtualisedRowListProps<T>): React.ReactElement {
  const parentRef = useRef<HTMLDivElement | null>(null);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimateSize,
    overscan,
    // Each row reports its real height once mounted; the row component
    // calls `measureElement` via the ref returned in the loop below.
    // The fixed-size estimate is just the bootstrap value.
    getItemKey: (index) => {
      const item = items[index];
      // Defensive — index can briefly fall outside the array during
      // a re-render where `count` and items race. Fall back to the
      // numeric index so React's key remains stable.
      return item ? getRowId(item) : `idx-${String(index)}`;
    },
  });

  // Fire `onScrollNearEnd` when the last visible row is within
  // `nearEndRows` of the end. Guarded so we don't spam the callback;
  // a `firedForLength` ref re-arms whenever the items length grows
  // (next page landed).
  const firedForLength = useRef<number>(-1);
  useEffect(() => {
    if (!onScrollNearEnd) return;
    const virtualItems = virtualizer.getVirtualItems();
    if (virtualItems.length === 0) return;
    const last = virtualItems[virtualItems.length - 1];
    if (!last) return;
    if (last.index >= items.length - nearEndRows) {
      if (firedForLength.current !== items.length) {
        firedForLength.current = items.length;
        onScrollNearEnd();
      }
    }
    // We deliberately depend on the virtualiser's tick (getVirtualItems
    // changes identity each scroll) so React re-evaluates on scroll.
    // Adding `virtualizer` to the deps is the documented pattern.
  }, [virtualizer, items.length, nearEndRows, onScrollNearEnd]);

  // Stable measureRef callback so each rendered row binds to
  // `virtualizer.measureElement` (variable-height support).
  const measureRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) virtualizer.measureElement(node);
    },
    [virtualizer],
  );

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  return (
    <Box
      ref={parentRef}
      // The primitive owns the scroll container. AC1 single-scrollbar
      // invariant is preserved by the parent `ListWidget` body
      // running `overflow: hidden` when a virtualised panel mounts.
      sx={{
        flex: "1 1 auto",
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        position: "relative",
        contain: "strict", // browsers can skip layout outside the box
      }}
      data-testid={testId}
      role="list"
      aria-label={ariaLabel}
    >
      <Box
        sx={{
          height: `${String(totalSize)}px`,
          width: "100%",
          position: "relative",
        }}
        data-testid={testId ? `${testId}-spacer` : undefined}
      >
        {virtualItems.map((virtualItem: VirtualItem) => {
          const item = items[virtualItem.index];
          if (!item) return null;
          return (
            <Box
              key={virtualItem.key}
              ref={measureRef}
              data-index={virtualItem.index}
              role="listitem"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${String(virtualItem.start)}px)`,
              }}
            >
              {renderRow(item, virtualItem.index)}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
